import { inject, Service, OnDestroy } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { KDateFormat, KDateIs, KDateParse } from '@kiwid-app/kiwid-date-typescript';
import { IKLabelType } from '@kiwid-app/kiwid-ui-lib-angular';
import { TranslateService } from '@ngx-translate/core';
import { EAccountType, EFinancialPixKey } from 'app/core/enums/account-type-bank';
import { EEquipmentType } from 'app/core/enums/equipment.enum';
import { EInventoryOrderStatus } from 'app/core/enums/inventory-order.enum';
import {
    ELocationSettingLabelIdentificationCode,
    ELocationSettingLabelIdentificationType,
    ELocationSettingLabelType,
} from 'app/core/enums/location-settings.enum';
import { EOrderOutsourceStatus } from 'app/core/enums/order-outsource.enum';
import { EOrderItemsType } from 'app/core/enums/report.enum';
import { ETransactionType } from 'app/core/enums/transaction-type.enum';
import { CountrySettingHelper } from 'app/core/helpers/country-setting.helper';
import { EPeopleType } from 'app/core/models/people/people';
import { TimezoneService } from 'app/core/services/timezone/timezone.service';
import { FuseTranslationLoaderService } from 'app/core/translate/translation-loader.service';
import { Subscription } from 'rxjs';
import { DATE_API } from '../const/date.const';
import { ECommissionCalcBasis, ECommissionIncludeType } from '../enums/commission.enum';
import { ECustomerActiveStatusType, ECustomerStatus } from '../enums/customers-status.enum';
import { ERoutePeriod, ERouteStatus } from '../enums/delivery-route.enum';
import { EDentalNotation } from '../enums/dental-notation.enum';
import { EEmployeeStatus, ERelativeTypes } from '../enums/employee.enum';
import { EFinancialTransferInterval } from '../enums/financial-account.enum';
import { EGatewayRegistrationType } from '../enums/gateway-registration.enum';
import { EGender } from '../enums/gender.enum';
import { EInvoiceType } from '../enums/invoice-type.enum';
import { ECustomerMovementKey } from '../enums/invoice.enum';
import { EKiwidPlanSoftwareLevel } from '../enums/kiwid-plan.enum';
import { EOrderProductionStatus } from '../enums/order-production-status.enum';
import { EOrderMediaType, EOrderStatus, EOrderType } from '../enums/order.enum';
import { ECreditCardBrand, EPaymentMethod } from '../enums/payment-method.enum';
import { EPhoneType } from '../enums/phone-type.enum';
import { EProductStockKey } from '../enums/product.enum';
import { ESupplierStatus, ESupplierType } from '../enums/supplier.enum';
import { ETransactionStatus } from '../enums/transaction.enum';
import { IKeyValueProps } from '../models/key-value/key-value';
import { IGetMovementDescription, IGetMovementSubDescription } from '../types/label-status-helper.types';
import { locale as english } from './i18n/en';
import { locale as spanish } from './i18n/es';
import { locale as portuguese } from './i18n/pt';
import { Util } from './util';

@Service()
export class LabelStatusHelper implements OnDestroy {
    private readonly translateService = inject(TranslateService);
    private readonly fuseTranslationLoaderService = inject(FuseTranslationLoaderService);
    private readonly iconRegistry = inject(MatIconRegistry);
    private readonly domSanitizer = inject(DomSanitizer);
    private readonly timezoneService = inject(TimezoneService);
    private readonly countrySettingHelper = inject(CountrySettingHelper);

    orderStatusForm: IKeyValueProps<string, EOrderStatus>[] = [];
    receivableStatusForm: IKeyValueProps<string, ETransactionStatus>[] = [];
    invoiceType: IKeyValueProps<string, EInvoiceType>[] = [];
    genderStatusForm: IKeyValueProps<string, EGender>[] = [];
    phoneTypes: IKeyValueProps<string, EPhoneType>[] = [];
    supplierTypes: IKeyValueProps<string, ESupplierType>[] = [];
    supplierStatus: IKeyValueProps<string, ESupplierStatus>[] = [];
    employeeStatus: IKeyValueProps<string, EEmployeeStatus>[] = [];
    customerStatus: IKeyValueProps<string, ECustomerStatus>[] = [];
    orderTypes: IKeyValueProps<string, EOrderType>[] = [];
    dentistryDentalNotation: IKeyValueProps<string, EDentalNotation>[] = [];
    registrationType: IKeyValueProps<string, EGatewayRegistrationType>[] = [];
    financialTransferInterval: IKeyValueProps<string, EFinancialTransferInterval>[] = [];
    paymentMethodsForm: IKeyValueProps<string, EPaymentMethod>[] = [];
    paymentMethodsFormIncome: IKeyValueProps<string, EPaymentMethod>[] = [];
    paymentMethodsFormIncomeKiwidPay: IKeyValueProps<string, EPaymentMethod>[] = [];
    paymentMethodsFormExpense: IKeyValueProps<string, EPaymentMethod>[] = [];
    accountBanksForm: IKeyValueProps<string, EAccountType>[] = [];
    financialPixForm: IKeyValueProps<string, EFinancialPixKey>[] = [];
    employeeRelativeTypes: IKeyValueProps<string, ERelativeTypes>[] = [];
    inventoryOrderStatus: IKeyValueProps<string, EInventoryOrderStatus>[] = [];
    commissionIncludeTypes: IKeyValueProps<string, ECommissionIncludeType>[] = [];
    commissionCalcBasis: IKeyValueProps<string, ECommissionCalcBasis>[] = [];
    orderItemTypes: IKeyValueProps<string, EOrderItemsType>[] = [];
    equipmentTypes: IKeyValueProps<string, EEquipmentType>[] = [];
    settingLabelTypeForm: IKeyValueProps<string, ELocationSettingLabelType>[] = [];
    settingLabelIdentificationTypeForm: IKeyValueProps<string, ELocationSettingLabelIdentificationType>[] = [];
    settingLabelIdentificationCodeForm: IKeyValueProps<string, ELocationSettingLabelIdentificationCode>[] = [];
    orderMediaTypeForm: IKeyValueProps<string, EOrderMediaType>[] = [];
    private readonly subscriptions = new Subscription();
    private translate: any;

    constructor() {
        this.refreshLabelHelper();
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    refreshLabelHelper(): void {
        this.registerTranslate();
        this.setOrderStatus();
        this.setReceivableStatus();
        this.setGenders();
        this.setOrderTypes();
        this.setPhoneTypes();
        this.setSupplierTypes();
        this.setSupplierStatus();
        this.setEmployeeStatus();
        this.setCustomerStatus();
        this.setInvoiceType();
        this.setDentistryDentalNotation();
        this.setRegistrationType();
        this.setFinancialTransferInterval();
        this.setPaymentMethodsForm();
        this.setAccountBanksForm();
        this.setFinancialPixForm();
        this.setEmployeeRelativeTypesForm();
        this.setInventoryOrderStatusForm();
        this.setCommissionIncludeTypes();
        this.setCommissionCalcBasis();
        this.setDescriptionOrderItemTypes();
        this.setEquipmentTypes();
        this.setSettingLabelTypeForm();
        this.setSettingLabelIdentificationCodeForm();
        this.setSettingLabelIdentificationTypeForm();
        this.setOrderMediaTypeForm();
    }

    static toCustomNumber(value: number, maxLength: number, digit?: number, code?: string): string {
        if (!value) {
            return null;
        }

        const codeString = !!code ? code : '';
        const digitString = !!digit ? `-${digit}` : '';

        const stringValue = value.toString();
        return `${codeString}${stringValue.padStart(maxLength, '0')}${digitString}`;
    }

    getDescriptionOrderItemType(type: EOrderItemsType): string {
        const getTypes = {
            [EOrderItemsType.PRODUCT]: Util.translateKey(this.translate, 'ORDER_ITEM_TYPES.PRODUCT'),
            [EOrderItemsType.SERVICE]: Util.translateKey(this.translate, 'ORDER_ITEM_TYPES.SERVICE'),
        };

        return getTypes[type] ?? 'undefined';
    }

    getDescriptionEquipmentType(type: EEquipmentType): string {
        const getTypes = {
            [EEquipmentType.SCANNER]: Util.translateKey(this.translate, 'EQUIPMENT_TYPE.SCANNER'),
            [EEquipmentType.MILLING]: Util.translateKey(this.translate, 'EQUIPMENT_TYPE.MILLING'),
            [EEquipmentType.PRINTER]: Util.translateKey(this.translate, 'EQUIPMENT_TYPE.PRINTER'),
            [EEquipmentType.FURNACE]: Util.translateKey(this.translate, 'EQUIPMENT_TYPE.FURNACE'),
            [EEquipmentType.OTHER]: Util.translateKey(this.translate, 'EQUIPMENT_TYPE.OTHER'),
        };

        return getTypes[type] ?? 'undefined';
    }

    private setSettingLabelTypeForm(): void {
        this.settingLabelTypeForm = [
            {
                value: ELocationSettingLabelType.BROTHER_62_30,
                key: Util.translateKey(this.translate, 'LABEL_TYPE.BROTHER_62_30'),
            },
            {
                value: ELocationSettingLabelType.PIMACO_28_51,
                key: Util.translateKey(this.translate, 'LABEL_TYPE.PIMACO_28_51'),
            },
            {
                value: ELocationSettingLabelType.PIMACO_28_89,
                key: Util.translateKey(this.translate, 'LABEL_TYPE.PIMACO_28_89'),
            },
            {
                value: ELocationSettingLabelType.PIMACO_36_89,
                key: Util.translateKey(this.translate, 'LABEL_TYPE.PIMACO_36_89'),
            },
            {
                value: ELocationSettingLabelType.PIMACO_54_101,
                key: Util.translateKey(this.translate, 'LABEL_TYPE.PIMACO_54_101'),
            },
            {
                value: ELocationSettingLabelType.ZEBRA_80_50,
                key: Util.translateKey(this.translate, 'LABEL_TYPE.ZEBRA_80_50'),
            },
            {
                value: ELocationSettingLabelType.ZEBRA_50_30,
                key: Util.translateKey(this.translate, 'LABEL_TYPE.ZEBRA_50_30'),
            },
            {
                value: ELocationSettingLabelType.ZEBRA_50_30_X2,
                key: Util.translateKey(this.translate, 'LABEL_TYPE.ZEBRA_50_30_X2'),
            },
        ];
    }

    private setSettingLabelIdentificationCodeForm(): void {
        this.settingLabelIdentificationCodeForm = [
            {
                value: ELocationSettingLabelIdentificationCode.QR_CODE,
                key: Util.translateKey(this.translate, 'LABEL_IDENTIFICATION_CODE.QR_CODE'),
            },
            {
                value: ELocationSettingLabelIdentificationCode.BAR_CODE,
                key: Util.translateKey(this.translate, 'LABEL_IDENTIFICATION_CODE.BAR_CODE'),
            },
        ];
    }

    private setSettingLabelIdentificationTypeForm(): void {
        this.settingLabelIdentificationTypeForm = [
            {
                value: ELocationSettingLabelIdentificationType.ORDER,
                key: Util.translateKey(this.translate, 'LABEL_IDENTIFICATION_TYPE.ORDER'),
            },
            {
                value: ELocationSettingLabelIdentificationType.ADDRESS,
                key: Util.translateKey(this.translate, 'LABEL_IDENTIFICATION_TYPE.ADDRESS'),
            },
            {
                value: ELocationSettingLabelIdentificationType.ITEMS,
                key: Util.translateKey(this.translate, 'LABEL_IDENTIFICATION_TYPE.ITEMS'),
            },
        ];
    }

    private setOrderMediaTypeForm(): void {
        this.orderMediaTypeForm = [
            {
                value: EOrderMediaType.PRIVATE,
                key: this.getMediaTypeText(EOrderMediaType.PRIVATE),
            },
            {
                value: EOrderMediaType.PUBLIC,
                key: this.getMediaTypeText(EOrderMediaType.PUBLIC),
            },
        ];
    }

    private setEquipmentTypes(): void {
        this.equipmentTypes = [
            {
                key: this.getDescriptionEquipmentType(EEquipmentType.SCANNER),
                value: EEquipmentType.SCANNER,
            },
            {
                key: this.getDescriptionEquipmentType(EEquipmentType.MILLING),
                value: EEquipmentType.MILLING,
            },
            {
                key: this.getDescriptionEquipmentType(EEquipmentType.PRINTER),
                value: EEquipmentType.PRINTER,
            },
            {
                key: this.getDescriptionEquipmentType(EEquipmentType.FURNACE),
                value: EEquipmentType.FURNACE,
            },
            {
                key: this.getDescriptionEquipmentType(EEquipmentType.OTHER),
                value: EEquipmentType.OTHER,
            },
        ];
    }

    private setDescriptionOrderItemTypes(): void {
        this.orderItemTypes = [
            {
                key: this.getDescriptionOrderItemType(EOrderItemsType.PRODUCT),
                value: EOrderItemsType.PRODUCT,
            },
            {
                key: this.getDescriptionOrderItemType(EOrderItemsType.SERVICE),
                value: EOrderItemsType.SERVICE,
            },
        ];
    }

    getCommissionIncludeTypeDescription(type: ECommissionIncludeType): string {
        const getTypes = {
            [ECommissionIncludeType.ORDER]: Util.translateKey(this.translate, 'COMMISSION_INCLUDE_TYPE.ORDER'),
            [ECommissionIncludeType.STAGE]: Util.translateKey(this.translate, 'COMMISSION_INCLUDE_TYPE.STAGE'),
        };

        return getTypes[type] ?? 'undefined';
    }

    private setCommissionIncludeTypes(): void {
        this.commissionIncludeTypes = [
            {
                key: this.getCommissionIncludeTypeDescription(ECommissionIncludeType.ORDER),
                value: ECommissionIncludeType.ORDER,
            },
            {
                key: this.getCommissionIncludeTypeDescription(ECommissionIncludeType.STAGE),
                value: ECommissionIncludeType.STAGE,
            },
        ];
    }

    getCommissionCalcBasisDescription(type: ECommissionCalcBasis): string {
        const getTypes = {
            [ECommissionCalcBasis.GROSS_VALUE]: Util.translateKey(this.translate, 'COMMISSION_CALC_BASIS.GROSS_VALUE'),
            [ECommissionCalcBasis.NET_VALUE]: Util.translateKey(this.translate, 'COMMISSION_CALC_BASIS.NET_VALUE'),
        };

        return getTypes[type] ?? 'undefined';
    }

    private setCommissionCalcBasis(): void {
        this.commissionCalcBasis = [
            {
                key: this.getCommissionCalcBasisDescription(ECommissionCalcBasis.GROSS_VALUE),
                value: ECommissionCalcBasis.GROSS_VALUE,
            },
            {
                key: this.getCommissionCalcBasisDescription(ECommissionCalcBasis.NET_VALUE),
                value: ECommissionCalcBasis.NET_VALUE,
            },
        ];
    }

    getEmployeeRelativeTypeDescription(type: ERelativeTypes): string {
        const descriptions = {
            [ERelativeTypes.PARTNER]: Util.translateKey(this.translate, 'RELATIVE_TYPES.PARTNER'),
            [ERelativeTypes.FATHER]: Util.translateKey(this.translate, 'RELATIVE_TYPES.FATHER'),
            [ERelativeTypes.MOTHER]: Util.translateKey(this.translate, 'RELATIVE_TYPES.MOTHER'),
            [ERelativeTypes.BROTHER_SISTER]: Util.translateKey(this.translate, 'RELATIVE_TYPES.BROTHER_SISTER'),
            [ERelativeTypes.SON_DAUGHTER]: Util.translateKey(this.translate, 'RELATIVE_TYPES.SON_DAUGHTER'),
            [ERelativeTypes.FRIEND]: Util.translateKey(this.translate, 'RELATIVE_TYPES.FRIEND'),
            [ERelativeTypes.OTHER]: Util.translateKey(this.translate, 'RELATIVE_TYPES.OTHER'),
        };

        return descriptions[type];
    }

    private setInventoryOrderStatusForm(): void {
        this.inventoryOrderStatus = [
            {
                key: this.getInventoryOrderStatusDescription(EInventoryOrderStatus.DRAFT),
                value: EInventoryOrderStatus.DRAFT,
            },
            {
                key: this.getInventoryOrderStatusDescription(EInventoryOrderStatus.ORDERED),
                value: EInventoryOrderStatus.ORDERED,
            },
            {
                key: this.getInventoryOrderStatusDescription(EInventoryOrderStatus.PARTIAL),
                value: EInventoryOrderStatus.PARTIAL,
            },
            {
                key: this.getInventoryOrderStatusDescription(EInventoryOrderStatus.RECEIVED),
                value: EInventoryOrderStatus.RECEIVED,
            },
            {
                key: this.getInventoryOrderStatusDescription(EInventoryOrderStatus.CANCELLED),
                value: EInventoryOrderStatus.CANCELLED,
            },
        ];
    }

    private setEmployeeRelativeTypesForm(): void {
        this.employeeRelativeTypes = [
            {
                key: this.getEmployeeRelativeTypeDescription(ERelativeTypes.PARTNER),
                value: ERelativeTypes.PARTNER,
            },
            {
                key: this.getEmployeeRelativeTypeDescription(ERelativeTypes.FATHER),
                value: ERelativeTypes.FATHER,
            },
            {
                key: this.getEmployeeRelativeTypeDescription(ERelativeTypes.MOTHER),
                value: ERelativeTypes.MOTHER,
            },
            {
                key: this.getEmployeeRelativeTypeDescription(ERelativeTypes.BROTHER_SISTER),
                value: ERelativeTypes.BROTHER_SISTER,
            },
            {
                key: this.getEmployeeRelativeTypeDescription(ERelativeTypes.SON_DAUGHTER),
                value: ERelativeTypes.SON_DAUGHTER,
            },
            {
                key: this.getEmployeeRelativeTypeDescription(ERelativeTypes.FRIEND),
                value: ERelativeTypes.FRIEND,
            },
            {
                key: this.getEmployeeRelativeTypeDescription(ERelativeTypes.OTHER),
                value: ERelativeTypes.OTHER,
            },
        ];
    }

    getFinancialFixDescription(data: EFinancialPixKey): string {
        return Util.translateKey(this.translate, `PIX_KEY.${data.toUpperCase()}`);
    }

    private setFinancialPixForm(): void {
        this.financialPixForm = [
            {
                key: this.getFinancialFixDescription(EFinancialPixKey.DOCUMENT),
                value: EFinancialPixKey.DOCUMENT,
            },
            {
                key: this.getFinancialFixDescription(EFinancialPixKey.PHONE),
                value: EFinancialPixKey.PHONE,
            },
            {
                key: this.getFinancialFixDescription(EFinancialPixKey.EMAIL),
                value: EFinancialPixKey.EMAIL,
            },
            {
                key: this.getFinancialFixDescription(EFinancialPixKey.RANDOM),
                value: EFinancialPixKey.RANDOM,
            },
        ];
    }

    getAccountBankDescription(data: EAccountType): string {
        return Util.translateKey(this.translate, `ACCOUNT_BANK.${data.toUpperCase()}`);
    }

    private setAccountBanksForm(): void {
        this.accountBanksForm = [
            {
                key: this.getAccountBankDescription(EAccountType.CHECKING_ACCOUNT),
                value: EAccountType.CHECKING_ACCOUNT,
            },
            {
                key: this.getAccountBankDescription(EAccountType.SAVINGS_ACCOUNT),
                value: EAccountType.SAVINGS_ACCOUNT,
            },
            {
                key: this.getAccountBankDescription(EAccountType.CASH_ACCOUNT),
                value: EAccountType.CASH_ACCOUNT,
            },
            {
                key: this.getAccountBankDescription(EAccountType.CREDIT_CARD_ACCOUNT),
                value: EAccountType.CREDIT_CARD_ACCOUNT,
            },
            {
                key: this.getAccountBankDescription(EAccountType.DIGITAL_ACCOUNT),
                value: EAccountType.DIGITAL_ACCOUNT,
            },
        ];
    }

    private setFinancialTransferInterval(): void {
        this.financialTransferInterval = [
            {
                value: EFinancialTransferInterval.DAILY,
                key: this.translate?.TRANSFER_INTERVAL?.DAILY,
            },
            {
                value: EFinancialTransferInterval.WEEKLY,
                key: this.translate?.TRANSFER_INTERVAL?.WEEKLY,
            },
            {
                value: EFinancialTransferInterval.MONTHLY,
                key: this.translate?.TRANSFER_INTERVAL?.MONTHLY,
            },
        ];
    }

    private setRegistrationType(): void {
        this.registrationType = [
            { value: EGatewayRegistrationType.PERSONAL, key: this.translate?.REGISTRATION_TYPE?.PERSONAL },
            { value: EGatewayRegistrationType.BUSINESS, key: this.translate?.REGISTRATION_TYPE?.BUSINESS },
        ];
    }

    private setDentistryDentalNotation(): void {
        this.dentistryDentalNotation = [
            { value: EDentalNotation.FDI_NOTATION, key: this.translate?.DENTISTRY_DENTAL_NOTATION?.FDI_NOTATION },
            { value: EDentalNotation.UNIVERSAL_SYSTEM, key: this.translate?.DENTISTRY_DENTAL_NOTATION?.UNIVERSAL_SYSTEM },
            { value: EDentalNotation.PALMER_NOTATION, key: this.translate?.DENTISTRY_DENTAL_NOTATION?.PALMER_NOTATION },
        ];
    }

    private setInvoiceType(): void {
        this.invoiceType = [
            { value: EInvoiceType.BIWEEKLY, key: this.translate?.INVOICE_TYPE?.BIWEEKLY },
            { value: EInvoiceType.MONTHLY, key: this.translate?.INVOICE_TYPE?.MONTHLY },
        ];
    }

    private setSupplierTypes(): void {
        this.supplierTypes = [
            {
                key: this.translate?.SUPPLIER_TYPES?.BUSINESS,
                value: ESupplierType.BUSINESS,
            },
            {
                key: this.translate?.SUPPLIER_TYPES?.NATURAL,
                value: ESupplierType.NATURAL,
            },
        ];
    }

    private setSupplierStatus(): void {
        this.supplierStatus = [
            {
                key: this.translate?.SUPPLIER_STATUS?.ACTIVE,
                value: ESupplierStatus.ACTIVE,
            },
            {
                key: this.translate?.SUPPLIER_STATUS?.INACTIVE,
                value: ESupplierStatus.INACTIVE,
            },
        ];
    }

    private setEmployeeStatus(): void {
        this.employeeStatus = [
            {
                key: this.translate?.EMPLOYEE_STATUS?.ACTIVE,
                value: EEmployeeStatus.ACTIVE,
            },
            {
                key: this.translate?.EMPLOYEE_STATUS?.INACTIVE,
                value: EEmployeeStatus.INACTIVE,
            },
        ];
    }

    private setCustomerStatus(): void {
        this.customerStatus = [
            {
                key: this.getCustomerText(ECustomerStatus.LEAD),
                value: ECustomerStatus.LEAD,
            },
            {
                key: this.getCustomerText(ECustomerStatus.ACTIVE),
                value: ECustomerStatus.ACTIVE,
            },
            {
                key: this.getCustomerText(ECustomerStatus.BLOCKED),
                value: ECustomerStatus.BLOCKED,
            },
            {
                key: this.getCustomerText(ECustomerStatus.INACTIVE),
                value: ECustomerStatus.INACTIVE,
            },
        ];
    }

    private setPhoneTypes(): void {
        this.phoneTypes = [
            {
                key: this.descriptionPhoneType(EPhoneType.HOME),
                value: EPhoneType.HOME,
            },
            {
                key: this.descriptionPhoneType(EPhoneType.MOBILE),
                value: EPhoneType.MOBILE,
            },
            {
                key: this.descriptionPhoneType(EPhoneType.WORK),
                value: EPhoneType.WORK,
            },
            {
                key: this.descriptionPhoneType(EPhoneType.OTHER),
                value: EPhoneType.OTHER,
            },
        ];
    }

    public descriptionPhoneType(type: EPhoneType): string {
        const types = {
            [EPhoneType.HOME]: Util.translateKey(this.translate, 'PHONE_TYPES.HOME'),
            [EPhoneType.MOBILE]: Util.translateKey(this.translate, 'PHONE_TYPES.MOBILE'),
            [EPhoneType.WORK]: Util.translateKey(this.translate, 'PHONE_TYPES.WORK'),
            [EPhoneType.OTHER]: Util.translateKey(this.translate, 'PHONE_TYPES.OTHER'),
        };

        return types[type];
    }

    private setOrderTypes(): void {
        // this.orderTypes = [
        //     {
        //         key: this.translate?.ORDER_TYPE?.ORDER,
        //         value: EOrderType.ORDER,
        //     },
        //     {
        //         key: this.translate?.ORDER_TYPE?.QUOTATION,
        //         value: EOrderType.QUOTATION,
        //     },
        // ];
    }

    private setGenders(): void {
        this.genderStatusForm = [
            {
                key: this.translate?.GENDER?.MALE,
                value: EGender.MALE,
            },
            {
                key: this.translate?.GENDER?.FEMALE,
                value: EGender.FEMALE,
            },
            {
                key: this.translate?.GENDER?.NOT_BINARY,
                value: EGender.NOT_BINARY,
            },
        ];
    }

    private setReceivableStatus(): void {
        this.receivableStatusForm = [
            {
                key: this.translate?.RECEIVABLE_STATUS?.PENDING,
                value: ETransactionStatus.PENDING,
            },
            {
                key: this.translate?.RECEIVABLE_STATUS?.CANCELLED,
                value: ETransactionStatus.CANCELLED,
            },
            {
                key: this.translate?.RECEIVABLE_STATUS?.PAID,
                value: ETransactionStatus.PAID,
            },
            {
                key: this.translate?.RECEIVABLE_STATUS?.REFUSED,
                value: ETransactionStatus.REFUSED,
            },
            {
                key: this.translate?.RECEIVABLE_STATUS?.REFUNDED,
                value: ETransactionStatus.REFUNDED,
            },
            {
                key: this.translate?.RECEIVABLE_STATUS?.DISPUTE,
                value: ETransactionStatus.DISPUTE,
            },
            {
                key: this.translate?.RECEIVABLE_STATUS?.CHARGEBACK,
                value: ETransactionStatus.CHARGED_BACK,
            },
        ];
    }

    private setOrderStatus(): void {
        this.orderStatusForm = [
            {
                key: this.translate?.ORDER_STATUS?.DRAFT,
                value: EOrderStatus.DRAFT,
            },
            {
                key: this.translate?.ORDER_STATUS?.NEW,
                value: EOrderStatus.NEW,
            },
            {
                key: this.translate?.ORDER_STATUS?.APPROVAL,
                value: EOrderStatus.APPROVAL,
            },
            {
                key: this.translate?.ORDER_STATUS?.PENDING,
                value: EOrderStatus.PENDING,
            },
            {
                key: this.translate?.ORDER_STATUS?.PRODUCTION,
                value: EOrderStatus.PRODUCTION,
            },
            {
                key: this.translate?.ORDER_STATUS?.PROOF,
                value: EOrderStatus.PROOF,
            },
            {
                key: this.translate?.ORDER_STATUS?.DELAYED,
                value: EOrderStatus.DELAYED,
            },
            {
                key: this.translate?.ORDER_STATUS?.SUSPENDED,
                value: EOrderStatus.SUSPENDED,
            },
            {
                key: this.translate?.ORDER_STATUS?.FINISHED,
                value: EOrderStatus.FINISHED,
            },
            // {
            //     key: this.translate?.ORDER_STATUS?.DELIVERED,
            //     value: EOrderStatus.DELIVERED,
            // },
            {
                key: this.translate?.ORDER_STATUS?.CANCELLED,
                value: EOrderStatus.CANCELLED,
            },
        ];
    }

    private setPaymentMethodsForm(): void {
        this.paymentMethodsForm = [
            {
                key: Util.translateKey(this.translate, 'PAYMENT_METHOD.CASH'),
                value: EPaymentMethod.CASH,
            },
            {
                key: Util.translateKey(this.translate, 'PAYMENT_METHOD.CHECK'),
                value: EPaymentMethod.CHECK,
            },
            {
                key: Util.translateKey(this.translate, 'PAYMENT_METHOD.ACCOUNT_CREDIT'),
                value: EPaymentMethod.ACCOUNT_CREDIT,
            },
            {
                key: Util.translateKey(this.translate, 'PAYMENT_METHOD.BANK_SLIP'),
                value: EPaymentMethod.BANK_SLIP,
            },
            {
                key: Util.translateKey(this.translate, 'PAYMENT_METHOD.CREDIT_CARD'),
                value: EPaymentMethod.CREDIT_CARD,
            },
            {
                key: Util.translateKey(this.translate, 'PAYMENT_METHOD.DEBIT_CARD'),
                value: EPaymentMethod.DEBIT_CARD,
            },
            {
                key: Util.translateKey(this.translate, 'PAYMENT_METHOD.ONLINE_CARD'),
                value: EPaymentMethod.ONLINE_CARD,
            },
            {
                key: Util.translateKey(this.translate, 'PAYMENT_METHOD.ONLINE_BANK_SLIP'),
                value: EPaymentMethod.ONLINE_BANK_SLIP,
            },
            {
                key: Util.translateKey(this.translate, 'PAYMENT_METHOD.TRANSFER'),
                value: EPaymentMethod.TRANSFER,
            },
            {
                key: Util.translateKey(this.translate, 'PAYMENT_METHOD.DIRECT_DEBIT'),
                value: EPaymentMethod.DIRECT_DEBIT,
            },
        ].filter((paymentMethod) => !this.countrySettingHelper.hiddenPaymentMethods.includes(paymentMethod.value));
        const incomes = [
            EPaymentMethod.CASH,
            EPaymentMethod.CHECK,
            EPaymentMethod.ACCOUNT_CREDIT,
            EPaymentMethod.BANK_SLIP,
            EPaymentMethod.CREDIT_CARD,
            EPaymentMethod.DEBIT_CARD,
        ];
        const incomeKiwidPay = [EPaymentMethod.ONLINE_CARD, EPaymentMethod.ONLINE_BANK_SLIP];
        const expenses = [
            EPaymentMethod.CASH,
            EPaymentMethod.CHECK,
            EPaymentMethod.BANK_SLIP,
            EPaymentMethod.CREDIT_CARD,
            EPaymentMethod.DEBIT_CARD,
            EPaymentMethod.TRANSFER,
            EPaymentMethod.DIRECT_DEBIT,
        ];
        this.paymentMethodsFormIncome = this.paymentMethodsForm.filter((paymentMethod) => incomes.includes(paymentMethod.value));
        this.paymentMethodsFormIncomeKiwidPay = this.paymentMethodsForm.filter((paymentMethod) => incomeKiwidPay.includes(paymentMethod.value));
        this.paymentMethodsFormExpense = this.paymentMethodsForm.filter((paymentMethod) => expenses.includes(paymentMethod.value));
    }

    getSupplierTypeName(type: ESupplierType): string {
        if (type === ESupplierType.NATURAL) {
            return this.translate?.SUPPLIER_TYPES?.NATURAL;
        }

        return this.translate?.SUPPLIER_TYPES?.BUSINESS;
    }

    getProductStockKey(key: EProductStockKey): string {
        const stockKeys = {
            [EProductStockKey.NEW_STOCK]: Util.translateKey(this.translate, 'STOCK_KEY.NEW_STOCK'),
            [EProductStockKey.RETURN]: Util.translateKey(this.translate, 'STOCK_KEY.RETURN'),
            [EProductStockKey.TRANSFER]: Util.translateKey(this.translate, 'STOCK_KEY.TRANSFER'),
            [EProductStockKey.ADJUSTMENT]: Util.translateKey(this.translate, 'STOCK_KEY.ADJUSTMENT'),
            [EProductStockKey.INTERNAL_USE]: Util.translateKey(this.translate, 'STOCK_KEY.INTERNAL_USE'),
            [EProductStockKey.DAMAGED]: Util.translateKey(this.translate, 'STOCK_KEY.DAMAGED'),
            [EProductStockKey.OUT_OF_DATE]: Util.translateKey(this.translate, 'STOCK_KEY.OUT_OF_DATE'),
            [EProductStockKey.LOST]: Util.translateKey(this.translate, 'STOCK_KEY.LOST'),
            [EProductStockKey.OTHER]: Util.translateKey(this.translate, 'STOCK_KEY.OTHER'),
            [EProductStockKey.STOCK_ENABLED]: Util.translateKey(this.translate, 'STOCK_KEY.STOCK_ENABLED'),
            [EProductStockKey.STOCK_DISABLED]: Util.translateKey(this.translate, 'STOCK_KEY.STOCK_DISABLED'),
            [EProductStockKey.SALE]: Util.translateKey(this.translate, 'STOCK_KEY.SALE'),
            [EProductStockKey.ORDER_REOPEN]: Util.translateKey(this.translate, 'STOCK_KEY.ORDER_REOPEN'),
            [EProductStockKey.ORDER_CANCELLED]: Util.translateKey(this.translate, 'STOCK_KEY.ORDER_CANCELLED'),
            [EProductStockKey.ORDER_DELETED]: Util.translateKey(this.translate, 'STOCK_KEY.ORDER_DELETED'),
            [EProductStockKey.ITEM_DELETED]: Util.translateKey(this.translate, 'STOCK_KEY.ITEM_DELETED'),
            [EProductStockKey.INVENTORY_ORDER_CANCELLED]: Util.translateKey(this.translate, 'STOCK_KEY.INVENTORY_ORDER_CANCELLED'),
            [EProductStockKey.INVENTORY_ORDER_DELETED]: Util.translateKey(this.translate, 'STOCK_KEY.INVENTORY_ORDER_DELETED'),
            [EProductStockKey.REMAKE]: Util.translateKey(this.translate, 'STOCK_KEY.REMAKE'),
        };

        return stockKeys[key] ?? 'undefined';
    }

    getInventoryOrderStatusDescription(status: EInventoryOrderStatus): string {
        const getStatus = {
            [EInventoryOrderStatus.DRAFT]: Util.translateKey(this.translate, 'INVENTORY_ORDER_STATUS.DRAFT'),
            [EInventoryOrderStatus.APPROVAL]: Util.translateKey(this.translate, 'INVENTORY_ORDER_STATUS.APPROVAL'),
            [EInventoryOrderStatus.ORDERED]: Util.translateKey(this.translate, 'INVENTORY_ORDER_STATUS.ORDERED'),
            [EInventoryOrderStatus.PARTIAL]: Util.translateKey(this.translate, 'INVENTORY_ORDER_STATUS.PARTIAL'),
            [EInventoryOrderStatus.RECEIVED]: Util.translateKey(this.translate, 'INVENTORY_ORDER_STATUS.RECEIVED'),
            [EInventoryOrderStatus.CANCELLED]: Util.translateKey(this.translate, 'INVENTORY_ORDER_STATUS.CANCELLED'),
        };

        return getStatus[status] ?? 'undefined';
    }

    getInventoryOrderStatusLabelType(status: EInventoryOrderStatus): IKLabelType {
        const getType = {
            [EInventoryOrderStatus.DRAFT]: 'neutral',
            [EInventoryOrderStatus.APPROVAL]: 'warning',
            [EInventoryOrderStatus.ORDERED]: 'info-light',
            [EInventoryOrderStatus.PARTIAL]: 'info',
            [EInventoryOrderStatus.RECEIVED]: 'success',
            [EInventoryOrderStatus.CANCELLED]: 'danger',
        };

        return (getType[status] as IKLabelType) ?? 'neutral';
    }

    getPeopleTypeLabelType(type: EPeopleType): IKLabelType {
        const getType = {
            [EPeopleType.CUSTOMER]: 'info-light',
            [EPeopleType.EMPLOYEE]: 'success',
            [EPeopleType.SUPPLIER]: 'purple',
        };

        return (getType[type] as IKLabelType) ?? 'neutral';
    }

    getPeopleTypeText(type: EPeopleType): string {
        const getType = {
            [EPeopleType.CUSTOMER]: Util.translateKey(this.translate, 'PEOPLE_TYPE.CUSTOMER'),
            [EPeopleType.EMPLOYEE]: Util.translateKey(this.translate, 'PEOPLE_TYPE.EMPLOYEE'),
            [EPeopleType.SUPPLIER]: Util.translateKey(this.translate, 'PEOPLE_TYPE.SUPPLIER'),
        };

        return getType[type] ?? '-';
    }

    getCreditCardNameByBrand(type: ECreditCardBrand): string {
        return this.translate?.CREDIT_CARD_BRAND[type.toUpperCase()] ?? type?.kToTitleCase() ?? '';
    }

    getCreditCardBrandAssetUrlImage(type: ECreditCardBrand): string {
        return `/assets/images/brand/${type}.svg`;
    }

    getInvoiceType(type: EInvoiceType): string {
        if (type === EInvoiceType.MONTHLY) {
            return this.translate?.INVOICE_TYPE?.MONTHLY;
        }

        return this.translate?.INVOICE_TYPE?.BIWEEKLY;
    }

    getPaymentMethodName(paymentMethod: EPaymentMethod): string {
        switch (paymentMethod) {
            case EPaymentMethod.CASH:
                return this.translate?.PAYMENT_METHOD?.CASH;
            case EPaymentMethod.CHECK:
                return this.translate?.PAYMENT_METHOD?.CHECK;
            case EPaymentMethod.ACCOUNT_CREDIT:
                return this.translate?.PAYMENT_METHOD?.ACCOUNT_CREDIT;
            case EPaymentMethod.BANK_SLIP:
                return this.translate?.PAYMENT_METHOD?.BANK_SLIP;
            case EPaymentMethod.CREDIT_CARD:
                return this.translate?.PAYMENT_METHOD?.CREDIT_CARD;
            case EPaymentMethod.DEBIT_CARD:
                return this.translate?.PAYMENT_METHOD?.DEBIT_CARD;
            case EPaymentMethod.ONLINE_CARD:
                return this.translate?.PAYMENT_METHOD?.ONLINE_CARD;
            case EPaymentMethod.ONLINE_BANK_SLIP:
                return this.translate?.PAYMENT_METHOD?.ONLINE_BANK_SLIP;
            case EPaymentMethod.TRANSFER:
                return this.translate?.PAYMENT_METHOD?.TRANSFER;
            case EPaymentMethod.DIRECT_DEBIT:
                return this.translate?.PAYMENT_METHOD?.DIRECT_DEBIT;
            case EPaymentMethod.ACCOUNT_TRANSFER:
                return this.translate?.PAYMENT_METHOD?.ACCOUNT_TRANSFER;
            case EPaymentMethod.PIX:
                return this.translate?.PAYMENT_METHOD?.PIX;
        }
    }

    getDeliveryRouteStatusText(status: ERouteStatus): string {
        const statusText = {
            [ERouteStatus.PENDING]: Util.translateKey(this.translate, 'DELIVERY_ROUTE_STATUS.PENDING'),
            [ERouteStatus.SCHEDULED]: Util.translateKey(this.translate, 'DELIVERY_ROUTE_STATUS.SCHEDULED'),
            [ERouteStatus.FINISHED]: Util.translateKey(this.translate, 'DELIVERY_ROUTE_STATUS.FINISHED'),
        };

        return statusText[status] ?? '';
    }

    getDeliveryRoutePeriodText(period: ERoutePeriod): string {
        const periods: Record<ERoutePeriod, string> = {
            [ERoutePeriod.AFTERNOON]: Util.translateKey(this.translate, 'DELIVERY_ROUTE_PERIOD.AFTERNOON'),
            [ERoutePeriod.MORNING]: Util.translateKey(this.translate, 'DELIVERY_ROUTE_PERIOD.MORNING'),
        };

        return periods[period];
    }

    getKiwidPlanSoftwareLevel(type: EKiwidPlanSoftwareLevel): string {
        const typeText = {
            [EKiwidPlanSoftwareLevel.PRO]: Util.translateKey(this.translate, 'KIWID_PLAN_SOFTWARE_LEVEL.PRO'),
            [EKiwidPlanSoftwareLevel.BASIC]: Util.translateKey(this.translate, 'KIWID_PLAN_SOFTWARE_LEVEL.BASIC'),
            [EKiwidPlanSoftwareLevel.ESSENTIAL]: Util.translateKey(this.translate, 'KIWID_PLAN_SOFTWARE_LEVEL.ESSENTIAL'),
        };

        return typeText[type] ?? '';
    }

    getMediaTypeText(type: EOrderMediaType): string {
        const types = {
            [EOrderMediaType.PRIVATE]: Util.translateKey(this.translate, 'MEDIA_TYPE.PRIVATE'),
            [EOrderMediaType.PUBLIC]: Util.translateKey(this.translate, 'MEDIA_TYPE.PUBLIC'),
        };

        return types[type] ?? '';
    }

    getMediaTypeLabelType(type: EOrderMediaType): IKLabelType {
        const types = {
            [EOrderMediaType.PRIVATE]: 'neutral-dark',
            [EOrderMediaType.PUBLIC]: 'neutral-dark',
        };

        return (types[type] as IKLabelType) ?? 'neutral-dark';
    }

    getKeyMovementsText(key: ECustomerMovementKey): string {
        const keys = {
            [ECustomerMovementKey.OTHER]: Util.translateKey(this.translate, 'KEY_MOVEMENTS.OTHER'),
            [ECustomerMovementKey.DISCOUNT]: Util.translateKey(this.translate, 'KEY_MOVEMENTS.DISCOUNT'),
            [ECustomerMovementKey.REFUND]: Util.translateKey(this.translate, 'KEY_MOVEMENTS.REFUND'),
            [ECustomerMovementKey.ORDER]: Util.translateKey(this.translate, 'KEY_MOVEMENTS.ORDER'),
            [ECustomerMovementKey.PAYMENT]: Util.translateKey(this.translate, 'KEY_MOVEMENTS.PAYMENT'),
            [ECustomerMovementKey.INITIAL_BALANCE]: Util.translateKey(this.translate, 'KEY_MOVEMENTS.INITIAL_BALANCE'),
        };

        return keys[key] ?? '';
    }

    getDeliveryRouteStatusColor(status: ERouteStatus): IKLabelType {
        const statusText = {
            [ERouteStatus.PENDING]: 'info-light',
            [ERouteStatus.SCHEDULED]: 'neutral',
            [ERouteStatus.FINISHED]: 'success',
        };

        return (statusText[status] ?? 'normal') as IKLabelType;
    }

    getSupplierStatusColor(status: ESupplierStatus): 'success' | 'normal' {
        if (status === ESupplierStatus.ACTIVE) {
            return 'success';
        }

        return 'normal';
    }

    getEmployeeStatusColor(status: EEmployeeStatus): 'success' | 'normal' {
        if (status === EEmployeeStatus.ACTIVE) {
            return 'success';
        }

        return 'normal';
    }

    getCustomerColor(status: ECustomerStatus): 'normal' | 'danger' | 'warning' | 'success' {
        switch (status) {
            case ECustomerStatus.ACTIVE:
                return 'success';
            case ECustomerStatus.LEAD:
                return 'warning';
            case ECustomerStatus.INACTIVE:
                return 'normal';
            case ECustomerStatus.BLOCKED:
                return 'danger';
        }
    }

    getOrderColor(status: EOrderStatus): IKLabelType {
        switch (status) {
            case EOrderStatus.DRAFT:
                return 'neutral';
            case EOrderStatus.NEW:
                return 'info';
            case EOrderStatus.APPROVAL:
                return 'warning';
            case EOrderStatus.PENDING:
                return 'info-light';
            case EOrderStatus.PRODUCTION:
                return 'success';
            case EOrderStatus.PROOF:
                return 'warning-dark';
            case EOrderStatus.DELAYED:
                return 'danger-dark';
            case EOrderStatus.FINISHED:
                return 'neutral-dark';
            case EOrderStatus.DELIVERED:
                return 'success-dark';
            case EOrderStatus.CANCELLED:
                return 'danger';
            case EOrderStatus.SUSPENDED:
                return 'purple-dark';
        }
    }

    getSupplierStatusText(status: ESupplierStatus): string {
        if (status === ESupplierStatus.ACTIVE) {
            return this.translate?.SUPPLIER_STATUS?.ACTIVE;
        }

        return this.translate?.SUPPLIER_STATUS?.INACTIVE;
    }

    getEmployeeStatusText(status: EEmployeeStatus): string {
        switch (status) {
            case EEmployeeStatus.ACTIVE:
                return this.translate?.EMPLOYEE_STATUS?.ACTIVE;
            case EEmployeeStatus.INACTIVE:
                return this.translate?.EMPLOYEE_STATUS?.INACTIVE;
        }
    }

    getOrderText(status: EOrderStatus): string {
        switch (status) {
            case EOrderStatus.DRAFT:
                return this.translate?.ORDER_STATUS?.DRAFT;
            case EOrderStatus.NEW:
                return this.translate?.ORDER_STATUS?.NEW;
            case EOrderStatus.APPROVAL:
                return this.translate?.ORDER_STATUS?.APPROVAL;
            case EOrderStatus.PENDING:
                return this.translate?.ORDER_STATUS?.PENDING;
            case EOrderStatus.PRODUCTION:
                return this.translate?.ORDER_STATUS?.PRODUCTION;
            case EOrderStatus.PROOF:
                return this.translate?.ORDER_STATUS?.PROOF;
            case EOrderStatus.DELAYED:
                return this.translate?.ORDER_STATUS?.DELAYED;
            case EOrderStatus.SUSPENDED:
                return this.translate?.ORDER_STATUS?.SUSPENDED;
            case EOrderStatus.FINISHED:
                return this.translate?.ORDER_STATUS?.FINISHED;
            case EOrderStatus.DELIVERED:
                return this.translate?.ORDER_STATUS?.DELIVERED;
            case EOrderStatus.CANCELLED:
                return this.translate?.ORDER_STATUS?.CANCELLED;
        }
    }

    getProductionStatusText(status: EOrderProductionStatus): string {
        switch (status) {
            case EOrderProductionStatus.PENDING:
                return this.translate?.PRODUCTION_STATUS?.PENDING;
            case EOrderProductionStatus.ACTIVE:
                return this.translate?.PRODUCTION_STATUS?.ACTIVE;
            case EOrderProductionStatus.FINISHED:
                return this.translate?.PRODUCTION_STATUS?.FINISHED;
            case EOrderProductionStatus.CANCELED:
                return this.translate?.PRODUCTION_STATUS?.CANCELED;
        }
    }

    getOrderMatIcon(status: EOrderStatus): '' {
        return '';
    }

    getCustomerText(status: ECustomerStatus, statusType?: ECustomerActiveStatusType): string {
        switch (status) {
            case ECustomerStatus.ACTIVE: {
                if (statusType === ECustomerActiveStatusType.TEMPORARY) {
                    return Util.translateKey(this.translate, 'CUSTOMER_STATUS.ACTIVE.TEMPORARY');
                }

                return Util.translateKey(this.translate, 'CUSTOMER_STATUS.ACTIVE.ACTIVE');
            }
            case ECustomerStatus.LEAD:
                return Util.translateKey(this.translate, 'CUSTOMER_STATUS.LEAD');
            case ECustomerStatus.INACTIVE:
                return Util.translateKey(this.translate, 'CUSTOMER_STATUS.INACTIVE');
            case ECustomerStatus.BLOCKED:
                return Util.translateKey(this.translate, 'CUSTOMER_STATUS.BLOCKED');
        }
    }

    getOutsourceStatusDescription(status: EOrderOutsourceStatus): string {
        const statusText = {
            [EOrderOutsourceStatus.PENDING]: Util.translateKey(this.translate, 'OUTSOURCE_STATUS.PENDING'),
            [EOrderOutsourceStatus.FINISH]: Util.translateKey(this.translate, 'OUTSOURCE_STATUS.FINISH'),
            [EOrderOutsourceStatus.DELAYED]: Util.translateKey(this.translate, 'OUTSOURCE_STATUS.DELAYED'),
        };

        return statusText[status] ?? '-';
    }

    getOutsourceStatusColor(status: EOrderOutsourceStatus): IKLabelType {
        const statusText = {
            [EOrderOutsourceStatus.PENDING]: 'info',
            [EOrderOutsourceStatus.FINISH]: 'success',
            [EOrderOutsourceStatus.DELAYED]: 'danger-dark',
        };

        return (statusText[status] ?? 'normal') as IKLabelType;
    }

    getCustomerMatIcon(status: ECustomerStatus): '' {
        return '';
    }

    getGenderText(gender: EGender): string {
        switch (gender) {
            case EGender.MALE:
                return this.translate?.GENDER?.MALE;
            case EGender.FEMALE:
                return this.translate?.GENDER?.FEMALE;
            case EGender.NOT_BINARY:
                return this.translate?.GENDER?.NOT_BINARY;
            default:
                return this.translate?.GENDER?.MALE;
        }
    }

    getPhoneText(type: EPhoneType): string {
        switch (type) {
            case EPhoneType.HOME:
                return this.translate?.PHONE?.HOME;
            case EPhoneType.MOBILE:
                return this.translate?.PHONE?.MOBILE;
            case EPhoneType.WORK:
                return this.translate?.PHONE?.WORK;
            case EPhoneType.OTHER:
                return this.translate?.PHONE?.OTHER;
            default:
                return this.translate?.PHONE?.OTHER;
        }
    }

    getPaymentMethodMatIcon(paymentMethod: EPaymentMethod): string {
        switch (paymentMethod) {
            case EPaymentMethod.CASH: {
                return 'kiwid:money';
            }
            case EPaymentMethod.CHECK: {
                return 'kiwid:check';
            }
            case EPaymentMethod.ONLINE_BANK_SLIP:
            case EPaymentMethod.BANK_SLIP: {
                return 'kiwid:payment-method-bank-slip';
            }
            case EPaymentMethod.ACCOUNT_CREDIT: {
                return 'kiwid:payment-method-bank-transfer';
            }
            case EPaymentMethod.CREDIT_CARD:
            case EPaymentMethod.DEBIT_CARD:
            case EPaymentMethod.ONLINE_CARD: {
                return 'kiwid:credit-card';
            }
            case EPaymentMethod.PIX: {
                return 'kiwid:pix';
            }
        }

        return 'kiwid:payment-method-undefined';
    }

    private registerTranslate(): void {
        this.fuseTranslationLoaderService.loadTranslations(portuguese, english, spanish);
        const subscription = this.translateService.get('HELPER.LABEL').subscribe((response: any) => {
            this.translate = response;
        });

        this.subscriptions.add(subscription);
    }

    getTransactionStatusIcon(status: ETransactionStatus, transactionStatus: ETransactionStatus | null = null): string {
        switch (status) {
            case ETransactionStatus.PAID: {
                if (transactionStatus === ETransactionStatus.CANCELLED) {
                    this.iconRegistry.addSvgIcon('c_undo', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/custom/undo.svg'));
                    return 'c_undo';
                }

                this.iconRegistry.addSvgIcon('c_check', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/custom/ic-check.svg'));
                return 'c_check';
            }
            case ETransactionStatus.PENDING: {
                this.iconRegistry.addSvgIcon('c_priority', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/custom/priority.svg'));
                return 'c_priority';
            }
            case ETransactionStatus.CANCELLED:
            case ETransactionStatus.REFUSED: {
                this.iconRegistry.addSvgIcon('c_x', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/custom/x.svg'));
                return 'c_x';
            }
            case ETransactionStatus.REFUNDED: {
                this.iconRegistry.addSvgIcon('c_undo', this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/custom/undo.svg'));
                return 'c_undo';
            }
            case ETransactionStatus.DISPUTE:
            case ETransactionStatus.CHARGED_BACK: {
                this.iconRegistry.addSvgIcon(
                    'c_credit_card',
                    this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/custom/credit-card.svg'),
                );
                return 'c_credit_card';
            }
        }

        this.iconRegistry.addSvgIcon(
            'c_undefined',
            this.domSanitizer.bypassSecurityTrustResourceUrl('assets/icons/custom/payment-method-undefined.svg'),
        );
        return 'c_undefined';
    }

    getTransactionStatusTooltip(status: ETransactionStatus, dueDate?: string, transactionStatus: ETransactionStatus | null = null): string {
        switch (status) {
            case ETransactionStatus.PENDING: {
                const dueDateDate = KDateParse({
                    format: DATE_API,
                    value: dueDate,
                });
                const nowDate = KDateParse({
                    format: DATE_API,
                    value: KDateFormat(new Date(), DATE_API),
                });

                if (KDateIs(dueDateDate, 'equal', nowDate)) {
                    return this.translate?.RECEIVABLE_TOOLTIP_STATUS?.DUE_DATE_TODAY;
                } else if (KDateIs(dueDateDate, 'before', nowDate)) {
                    return this.translate?.RECEIVABLE_TOOLTIP_STATUS?.OVERDUE;
                }

                return this.translate?.RECEIVABLE_TOOLTIP_STATUS?.PENDING;
            }
            case ETransactionStatus.PAID: {
                if (transactionStatus === ETransactionStatus.CANCELLED) {
                    return this.translate?.RECEIVABLE_TOOLTIP_STATUS?.CANCELLED;
                }

                return this.translate?.RECEIVABLE_TOOLTIP_STATUS?.PAID;
            }
            case ETransactionStatus.CANCELLED: {
                return this.translate?.RECEIVABLE_TOOLTIP_STATUS?.CANCELLED;
            }
            case ETransactionStatus.REFUSED: {
                return this.translate?.RECEIVABLE_TOOLTIP_STATUS?.REFUSED;
            }
            case ETransactionStatus.REFUNDED: {
                return this.translate?.RECEIVABLE_TOOLTIP_STATUS?.REFUNDED;
            }
            case ETransactionStatus.DISPUTE: {
                return this.translate?.RECEIVABLE_TOOLTIP_STATUS?.DISPUTE;
            }
            case ETransactionStatus.CHARGED_BACK: {
                return this.translate?.RECEIVABLE_TOOLTIP_STATUS?.CHARGEBACK;
            }
            default:
                return '';
        }
    }

    getTransactionStatus(status: ETransactionStatus): string {
        switch (status) {
            case ETransactionStatus.PENDING: {
                return this.translate?.RECEIVABLE_STATUS?.PENDING;
            }
            case ETransactionStatus.PAID: {
                return this.translate?.RECEIVABLE_STATUS?.PAID;
            }
            case ETransactionStatus.CANCELLED: {
                return this.translate?.RECEIVABLE_STATUS?.CANCELLED;
            }
            case ETransactionStatus.REFUSED: {
                return this.translate?.RECEIVABLE_STATUS?.REFUSED;
            }
            case ETransactionStatus.REFUNDED: {
                return this.translate?.RECEIVABLE_STATUS?.REFUNDED;
            }
            case ETransactionStatus.DISPUTE: {
                return this.translate?.RECEIVABLE_STATUS?.DISPUTE;
            }
            case ETransactionStatus.CHARGED_BACK: {
                return this.translate?.RECEIVABLE_STATUS?.CHARGEBACK;
            }
            default:
                return '';
        }
    }

    getTranslateNumberOdontogram(toothNumber: number, typeDentalNotation: EDentalNotation): string {
        if (typeDentalNotation === EDentalNotation.FDI_NOTATION) {
            if (toothNumber <= 8) {
                return String(19 - toothNumber);
            }

            if (toothNumber <= 16) {
                return String(12 + toothNumber);
            }

            if (toothNumber <= 24) {
                return String(38 + (17 - toothNumber));
            }

            return String(16 + toothNumber);
        }

        if (typeDentalNotation === EDentalNotation.PALMER_NOTATION) {
            if (toothNumber <= 8) {
                return `UR${9 - toothNumber}`;
            }

            if (toothNumber <= 16) {
                return `UL${toothNumber - 8}`;
            }

            if (toothNumber <= 24) {
                return `LL${8 + (17 - toothNumber)}`;
            }

            return `LR${toothNumber - 24}`;
        }

        return String(toothNumber);
    }

    getMovementDescription(data: IGetMovementDescription): string | null {
        const { paymentMethodId, status, key, installment, paymentFrequencyTotal, type } = data;

        if (installment) {
            if (status === ETransactionStatus.CANCELLED) {
                return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.PAYMENT_INSTALLMENT_DESCRIPTION.CANCELLED', {
                    installmentNumber: String(installment.number),
                });
            }

            return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.PAYMENT_INSTALLMENT_DESCRIPTION.DEFAULT', {
                installment: String(installment.total),
            });
        }

        const paymentMethods = {
            [EPaymentMethod.CASH]: () => {
                return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.PAYMENT');
            },
            [EPaymentMethod.CHECK]: () => {
                if (key === ECustomerMovementKey.PAYMENT) {
                    return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.PAYMENT');
                }

                return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.CANCEL');
            },
            [EPaymentMethod.ACCOUNT_CREDIT]: () => {
                return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.PAYMENT');
            },
            [EPaymentMethod.BANK_SLIP]: () => {
                if (key === ECustomerMovementKey.PAYMENT) {
                    if (status === ETransactionStatus.PAID) {
                        return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.PAYMENT');
                    }

                    return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.GENERATE');
                }

                return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.CANCEL');
            },
            [EPaymentMethod.CREDIT_CARD]: () => {
                if (key === ECustomerMovementKey.PAYMENT) {
                    if (!!installment) {
                        return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.PAYMENT_INSTALLMENT', {
                            installment: `${paymentFrequencyTotal}`,
                        });
                    }

                    return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.PAYMENT_CASH');
                }

                return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.CANCEL');
            },
            [EPaymentMethod.DEBIT_CARD]: () => {
                if (key === ECustomerMovementKey.PAYMENT) {
                    return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.PAYMENT');
                }

                return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.REFUND');
            },
            [EPaymentMethod.ONLINE_CARD]: () => {
                if (key === ECustomerMovementKey.PAYMENT) {
                    return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.PAYMENT');
                }

                return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.REFUND');
            },
            [EPaymentMethod.ONLINE_BANK_SLIP]: () => {
                if (key === ECustomerMovementKey.PAYMENT) {
                    if (status === ETransactionStatus.PAID) {
                        return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.PAYMENT');
                    }

                    return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.GENERATE');
                }

                return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.CANCEL');
            },
            [EPaymentMethod.TRANSFER]: () => {
                if (type === ETransactionType.EXPENSE) {
                    return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.REFUND_PAYMENT');
                }

                return Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_DESCRIPTION.PAYMENT');
            },
            [EPaymentMethod.DIRECT_DEBIT]: () => {
                return null;
            },
        };

        if (![ECustomerMovementKey.PAYMENT, ECustomerMovementKey.PAYMENT_REFUND].includes(key)) {
            return null;
        }

        return paymentMethods[paymentMethodId]();
    }

    getMovementDescriptionSubTitle(data: IGetMovementSubDescription): string | null {
        const accountName = !!data.accountName
            ? Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_SUB_DESCRIPTION.ACCOUNT_NAME', { accountName: data.accountName })
            : undefined;
        const nf = !!data.externalInvoiceNumber
            ? Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_SUB_DESCRIPTION.NF_NAME', { nfName: data.externalInvoiceNumber })
            : undefined;
        const dueDate = !!data.dueDate
            ? Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_SUB_DESCRIPTION.DUE_DATE', {
                  dueDate: KDateFormat(
                      KDateParse({ value: data.dueDate, format: DATE_API }),
                      Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_SUB_DESCRIPTION.DATE_FORMAT'),
                  ),
              })
            : undefined;
        const methodType = !!data.methodTypeName ? data.methodTypeName : undefined;
        const checks: string[] = [];

        if (data.enableCheck) {
            const { check } = data;

            if (!!check?.number) {
                checks.push(`${Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_SUB_DESCRIPTION.CHECK.NUMBER')}: ${check.number}`);
            }

            if (!!check?.name) {
                checks.push(`${Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_SUB_DESCRIPTION.CHECK.NAME')}: ${check.name}`);
            }

            if (!!check?.date) {
                checks.push(
                    `${Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_SUB_DESCRIPTION.CHECK.DATE')}: ${KDateFormat(
                        {
                            value: check?.date,
                            format: DATE_API,
                        },
                        Util.translateKey(this.translate, 'MOVEMENT_PAYMENT_SUB_DESCRIPTION.DATE_FORMAT'),
                    )}`,
                );
            }
        }

        const paymentMethods = {
            [EPaymentMethod.CASH]: [accountName, nf].filter(Boolean).join(' | '),
            [EPaymentMethod.CHECK]: [accountName, ...checks, nf].filter(Boolean).join(' | '),
            [EPaymentMethod.ACCOUNT_CREDIT]: [accountName, nf].filter(Boolean).join(' | '),
            [EPaymentMethod.BANK_SLIP]: [accountName, dueDate, nf].filter(Boolean).join(' | '),
            [EPaymentMethod.CREDIT_CARD]: [methodType, nf].filter(Boolean).join(' | '),
            [EPaymentMethod.DEBIT_CARD]: [methodType, nf].filter(Boolean).join(' | '),
            [EPaymentMethod.ONLINE_CARD]: [methodType, nf].filter(Boolean).join(' | '),
            [EPaymentMethod.ONLINE_BANK_SLIP]: [accountName, dueDate, nf].filter(Boolean).join(' | '),
            [EPaymentMethod.TRANSFER]: ``,
            [EPaymentMethod.DIRECT_DEBIT]: ``,
        };

        if (!data.paymentMethodId) {
            return null;
        }

        return paymentMethods[data.paymentMethodId] ?? null;
    }

    getLambdaTitle(lambdaFunction: string): string {
        const lambdaNameArray = lambdaFunction.split('-');
        lambdaNameArray.splice(lambdaNameArray.length - 1, 1);

        return Util.translateKey(this.translate, `LAMBDA.${lambdaNameArray.join('_').toUpperCase()}`);
    }
}
